import classNames from 'classnames';
import {
  ArrowUturnLeftIcon,
  ArrowUturnRightIcon,
  ChevronDownIcon,
  StarIcon,
  TrashIcon,
} from '@heroicons/react/16/solid';
import { useTranslation } from 'react-i18next';
import { useEffect, useRef, useState } from 'react';
import { Dropdown, Loading } from 'react-daisyui';
import { eChannelType } from '../../../../../../../../../../providers/SocketsProvider/Sockets.enums';
import { EventType } from '../../../../../../../../Chat.enum';
import { isWapiUnsupportedInfoNote } from '../../../../../../../../Chat.utils';
import { useChatContext } from '../../../../../../../../Chat.hook';
import useMessageBubbleMenu from '../../../MessageBubbleMenu/MessageBubbleMenu.hook';
import './MessageMenu.styles.scss';
import { tMessageMenuProps } from './MessageMenu.types';
import {
  getMessageMenuBounds,
  pickMessageMenuPlacement,
  tMessageMenuPlacement,
} from './MessageMenu.functions';
import { useUserContext } from '../../../../../../../../../../providers/UserProvider/User.context';
import { eUserRole } from '../../../../../../../../../../providers/UserProvider/User.enums';
import { useIntegrationsContext } from '../../../../../../../../../../providers/IntegrationsProvider/Integrations.context';
import { eAlertType } from '../../../../../../../../../../providers/ToastProvider/Toast.enum';
import { useToastContext } from '../../../../../../../../../../providers/ToastProvider/Toast.hook';

export default function MessageMenu(props: tMessageMenuProps) {
  const { t } = useTranslation('chat/chat');
  const { chatEvent, placement } = props;
  const { message } = chatEvent;
  const { highlightMessages, deleteMessage, loading } = useMessageBubbleMenu();
  const { setReplyMessage, setIsResending, chatEvents, selectedChat } =
    useChatContext();
  const { whatsappIntegration, facebookIntegration, instagramIntegration } =
    useIntegrationsContext();
  const { addAlert } = useToastContext();
  const { currentUserRole } = useUserContext();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const isSenderMessage = Boolean(message?.fromMe);

  const isTheLastMessageOnChat =
    chatEvents.findIndex((event) => event.id === chatEvent.id) ===
    chatEvents.length - 1;
  const hasQuotedMessage = Boolean(chatEvent?.content?.quotedEventID);
  const messageText = chatEvent?.content?.text || '';
  const isLongMessage = messageText.length >= 100;
  // BUG-1156: measured on open, never derived from the message — see
  // pickMessageMenuPlacement.
  const [menuPlacement, setMenuPlacement] = useState<tMessageMenuPlacement>({
    horizontal: 'left',
  });
  const verticalPosition =
    isTheLastMessageOnChat && hasQuotedMessage && !isLongMessage
      ? 'top'
      : undefined;

  const isConnected =
    whatsappIntegration.state === 'CONNECTED' ||
    facebookIntegration.state === 'CONNECTED' ||
    instagramIntegration.state === 'CONNECTED';
  const isSellerUser = currentUserRole === eUserRole.SELLER;

  const shouldShowDeleteAndForwardButtons =
    selectedChat?.integration === eChannelType.WHATSAPP_WEB;

  function closeMenu() {
    setIsOpen(false);
    const dropdown = dropdownRef.current?.closest('.dropdown');
    dropdown?.classList.remove('dropdown-open');
  }

  function toggleMenu(event: React.MouseEvent<HTMLLabelElement>) {
    const currentButton = event.currentTarget;
    const currentDropdown = currentButton?.closest('.dropdown');
    const isDropdownOpen = currentDropdown?.classList.contains('dropdown-open');

    if (isDropdownOpen) {
      closeMenu();
    } else {
      setMenuPlacement(
        pickMessageMenuPlacement(
          currentButton.getBoundingClientRect(),
          getMessageMenuBounds(currentButton),
        ),
      );
      setIsOpen(true);
      currentDropdown?.classList.add('dropdown-open');
    }
  }

  function onClickHighlightMessage() {
    highlightMessages(chatEvent.id, {
      type: EventType.MESSAGE,
      is_favorite: !message?.isFavorite,
    });
    closeMenu();
  }

  function onClickReply() {
    setReplyMessage(chatEvent);
    closeMenu();
  }

  function onClickReSend() {
    setIsResending(true);
    closeMenu();
  }

  async function onClickDelete() {
    const integration = selectedChat?.integration || eChannelType.WHATSAPP_WEB;

    if (!isConnected) {
      return addAlert({
        type: eAlertType.ERROR,
        message: t('chatMenu.deleteMessage'),
      });
    }
    await deleteMessage(chatEvent.id, integration);
    setIsOpen(false);
  }

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        closeMenu();
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // BUG-704: 3063/3064 bubbles are synthetic informational notes with no real
  // message content — there is nothing to reply-quote, star, forward/resend, or
  // delete. Hide the whole per-message menu (the emoji-react button is gated
  // separately in MessageBubbleV2).
  if (isWapiUnsupportedInfoNote(message?.error)) return null;

  // BUG-1063: same reasoning on a deleted message's tombstone. Nothing is left
  // to quote, forward or delete, and "Destacar" patches the event with
  // `type: MESSAGE` — it would turn the tombstone back into a message whose
  // body is already gone.
  if (
    chatEvent.type === EventType.OUTBOUND_DELETED ||
    chatEvent.type === EventType.INBOUND_DELETED
  ) {
    return null;
  }

  return (
    <Dropdown
      ref={dropdownRef}
      open={isOpen}
      horizontal={menuPlacement.horizontal}
      end={menuPlacement.end}
      // The last-message rule wins; otherwise "below" when neither side fits.
      vertical={verticalPosition ?? menuPlacement.vertical}
      className={classNames('MessageMenu', {
        'MessageMenu--right': placement === 'end',
      })}
      dataTheme="light"
    >
      <Dropdown.Toggle color="ghost" size="xs" onClick={toggleMenu}>
        <ChevronDownIcon className="w-5 h-5" />
      </Dropdown.Toggle>

      {isOpen && (
        // w-52 is MESSAGE_MENU_WIDTH_PX: change both together.
        <Dropdown.Menu className="w-52 z-10">
          <Dropdown.Item onClick={onClickReply} color="neutral">
            <ArrowUturnLeftIcon className="w-5 h-5" />
            {t('messageMenu.reply')}
          </Dropdown.Item>

          <Dropdown.Item onClick={onClickHighlightMessage}>
            <StarIcon className="w-5 h-5" />{' '}
            {message?.isFavorite
              ? t('messageMenu.removeHighlight')
              : t('messageMenu.highlight')}
          </Dropdown.Item>

          <Dropdown.Item onClick={onClickReSend}>
            <ArrowUturnRightIcon className="w-5 h-5" />{' '}
            {t('messageMenu.sendToAnotherChat')}
          </Dropdown.Item>

          {shouldShowDeleteAndForwardButtons &&
            isSenderMessage &&
            !isSellerUser && (
              <Dropdown.Item onClick={onClickDelete}>
                {loading ? (
                  <Loading size="xs" color="primary" />
                ) : (
                  <TrashIcon className="w-5 h-5" />
                )}
                {t('messageMenu.delete')}
              </Dropdown.Item>
            )}
        </Dropdown.Menu>
      )}
    </Dropdown>
  );
}
